import { useState } from 'react';
import { PrimaryButton } from '@/lib';
import { useCreateSite } from '../queries';

const TIMEZONES = ['America/Los_Angeles', 'America/Denver', 'America/Chicago', 'America/New_York', 'UTC', 'Europe/Berlin'];

export function SiteStep({ onCompleted }: { onCompleted: () => void }) {
  const create = useCreateSite();
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [timezone, setTimezone] = useState<string>(
    typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'UTC',
  );
  const [savedSites, setSavedSites] = useState<string[]>([]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    const out = await create.mutateAsync({ name: name.trim(), address, timezone });
    setSavedSites((prev) => [...prev, name.trim()]);
    setName('');
    setAddress('');
    onCompleted();
    return out;
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-sp-4">
      <p className="text-fs-body text-gray-700">
        Sites are the physical locations your assets live at. Every asset
        belongs to one site, and the Fleet Map groups assets by site.
      </p>

      {savedSites.length > 0 ? (
        <ul className="flex flex-wrap gap-sp-2 font-mono text-fs-meta">
          {savedSites.map((s) => (
            <li key={s} className="rounded-r-chip bg-healthy-bg px-sp-2 py-sp-1 text-healthy-fg">
              ✓ {s}
            </li>
          ))}
        </ul>
      ) : null}

      <label className="flex flex-col gap-sp-1 text-fs-body text-gray-900">
        Site name
        <input
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Plant A"
          className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-2 py-sp-1"
        />
      </label>
      <label className="flex flex-col gap-sp-1 text-fs-body text-gray-900">
        Address (optional)
        <input
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="123 Mill Road"
          className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-2 py-sp-1"
        />
      </label>
      <label className="flex flex-col gap-sp-1 text-fs-body text-gray-900">
        Timezone
        <select
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
          className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-2 py-sp-1"
        >
          {TIMEZONES.map((tz) => (
            <option key={tz}>{tz}</option>
          ))}
        </select>
      </label>

      <div className="flex items-center gap-sp-3">
        <PrimaryButton
          type="submit"
          label={create.isPending ? 'Saving…' : savedSites.length === 0 ? 'Save site' : 'Add another site'}
          disabled={create.isPending}
        />
        {create.isError ? (
          <span role="alert" className="text-fs-meta text-critical">
            {String(create.error)}
          </span>
        ) : null}
      </div>
    </form>
  );
}
